import type { SlugStyle } from "omuso";
import React from "react";

const BASIC_EXAMPLE = `# Hello World

This is a simple paragraph.

## IX. Capítulo primero

Another paragraph with *italic text*.`;

const SLUG_STYLES: { value: SlugStyle; label: string }[] = [
	{ value: "kebab", label: "Kebab" },
	{ value: "wiki", label: "Wiki" },
	{ value: "none", label: "None" },
];

export function APITester() {
	const responseRef = React.useRef<HTMLTextAreaElement>(null);
	const statusRef = React.useRef<HTMLInputElement>(null);

	const [errorMessage, setErrorMessage] = React.useState<string | null>(null);
	const [cooldown, setCooldown] = React.useState<number>(0);
	const [slugStyle, setSlugStyle] = React.useState<SlugStyle>("kebab");

	const markdownRef = React.useRef<string>(BASIC_EXAMPLE);

	const cooldownRef = React.useRef<NodeJS.Timeout | null>(null);
	const debounceRef = React.useRef<NodeJS.Timeout | null>(null);

	const startCooldown = (seconds: number) => {
		setCooldown(seconds);
		setErrorMessage(`Request limit reached. Please wait ${seconds}s...`);

		if (cooldownRef.current) clearInterval(cooldownRef.current);

		cooldownRef.current = setInterval(() => {
			setCooldown((prev) => {
				if (prev <= 1) {
					clearInterval(cooldownRef.current!);
					setErrorMessage(null);
					return 0;
				}
				setErrorMessage(`Request limit reached. Please wait ${prev - 1}s...`);
				return prev - 1;
			});
		}, 1000);
	};

	const testEndpoint = async (
		markdown: string,
		style: SlugStyle = slugStyle,
	) => {
		if (cooldown > 0) return;

		if (!markdown.trim()) {
			if (responseRef.current) responseRef.current.value = "";
			if (statusRef.current) statusRef.current.value = "";
			return;
		}

		try {
			const res = await fetch("/api/parser", {
				method: "POST",
				body: JSON.stringify({ markdown, options: { slugStyle: style } }),
			});

			if (res.status === 429) {
				const waitSeconds = 10;
				startCooldown(waitSeconds);
				return;
			}

			if (!res.ok) throw new Error(`HTTP ${res.status}`);
			const data = await res.json();

			if (responseRef.current) {
				responseRef.current.value = JSON.stringify(
					data.parsedMarkdown,
					null,
					2,
				);
				responseRef.current.scrollTop = responseRef.current.scrollHeight;
			}

			if (statusRef.current) {
				const elapsed = Number(data.elapsedTime).toFixed(2);
				statusRef.current.value = `Parsed in ${elapsed}ms`;
			}

			setErrorMessage(null);
		} catch (error: any) {
			setErrorMessage(`Error: ${error.message || String(error)}`);
			if (statusRef.current) statusRef.current.value = "Failed to parse";
		}
	};

	const handleInputChange = (value: string) => {
		markdownRef.current = value;
		const length = value.length;

		if (debounceRef.current) clearTimeout(debounceRef.current);

		let delay = 0;
		if (length > 2000) delay = 400;

		if (delay > 0) {
			debounceRef.current = setTimeout(() => testEndpoint(value), delay);
		} else {
			testEndpoint(value);
		}
	};

	const handleSlugStyleChange = (value: SlugStyle) => {
		setSlugStyle(value);
		testEndpoint(markdownRef.current, value);
	};

	React.useEffect(() => {
		testEndpoint(BASIC_EXAMPLE);

		return () => {
			if (cooldownRef.current) clearInterval(cooldownRef.current);
			if (debounceRef.current) clearTimeout(debounceRef.current);
		};
	}, []);

	return (
		<>
			<nav>
				<label>
					<span>Slug style</span>
					<select
						name="slug-style"
						value={slugStyle}
						onChange={(e) => handleSlugStyleChange(e.target.value as SlugStyle)}
						className="va-select"
					>
						{SLUG_STYLES.map(({ value, label }) => (
							<option key={value} value={value}>
								{label}
							</option>
						))}
					</select>
				</label>
			</nav>
			<main>
				<textarea
					name="input"
					onChange={(e) => handleInputChange(e.target.value)}
					placeholder="Write markdown here..."
					defaultValue={BASIC_EXAMPLE}
					className="va-input"
				/>
				<textarea
					name="output"
					ref={responseRef}
					readOnly
					className="va-input"
				/>
			</main>
			<footer>
				<input name="parse-time" ref={statusRef} readOnly />
				{errorMessage && <p>{errorMessage}</p>}
			</footer>
		</>
	);
}
